import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/state/auth";
import { apiRequest } from "@/utils/apiClient";
import { NewsLabel, NewsPost, NewsPostInput, NewsPostSummary } from "@/types/news";

const QUERY_ROOT = ["admin", "news"] as const;

export const useNewsLabels = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, "labels"],
    queryFn: async () => {
      const data = await apiRequest<{ labels: NewsLabel[] }>("/api/admin/news/labels");
      return data.labels;
    },
    enabled: Boolean(profile),
  });
};

export const useCreateNewsLabel = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, slug }: { name: string; slug?: string | null }) => {
      const { label } = await apiRequest<{ label: NewsLabel }>("/api/admin/news/labels", {
        method: "POST",
        body: JSON.stringify({ name, slug }),
      });
      return label;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "labels"] });
    },
  });
};

export const useDeleteNewsLabel = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (labelId: string) => {
      await apiRequest<void>(`/api/admin/news/labels/${labelId}`, { method: "DELETE" });
      return labelId;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "labels"] });
    },
  });
};

export const useNewsPosts = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, "posts"],
    queryFn: async () => {
      const data = await apiRequest<{ posts: NewsPostSummary[] }>(
        "/api/admin/news/posts?include_unpublished=true",
      );
      return data.posts;
    },
    enabled: Boolean(profile),
  });
};

export const useNewsPost = (postId: string | null) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, "post", postId],
    queryFn: async () => {
      if (!postId) {
        return null;
      }
      const data = await apiRequest<{ post: NewsPost }>(`/api/admin/news/posts/${postId}`);
      return data.post;
    },
    enabled: Boolean(profile && postId),
  });
};

export const useCreateNewsPost = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NewsPostInput) => {
      const { post } = await apiRequest<{ post: NewsPost }>("/api/admin/news/posts", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return post;
    },
    onSuccess: (post) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "posts"] });
      client.setQueryData([...QUERY_ROOT, "post", post.postId], post);
    },
  });
};

export const useUpdateNewsPost = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, payload }: { postId: string; payload: Partial<NewsPostInput> }) => {
      const { post } = await apiRequest<{ post: NewsPost }>(`/api/admin/news/posts/${postId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      return post;
    },
    onSuccess: (post) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "posts"] });
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "post", post.postId] });
    },
  });
};

export const useDeleteNewsPost = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      await apiRequest<void>(`/api/admin/news/posts/${postId}`, { method: "DELETE" });
      return postId;
    },
    onSuccess: (postId) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "posts"] });
      client.removeQueries({ queryKey: [...QUERY_ROOT, "post", postId] });
    },
  });
};

export const useSearchNewsPosts = (query: string, enabled: boolean) => {
  return useQuery({
    queryKey: [...QUERY_ROOT, "search", query],
    queryFn: async () => {
      if (!query) {
        return [] as NewsPostSummary[];
      }
      const data = await apiRequest<{ results: NewsPostSummary[] }>(
        `/api/admin/news/posts/search?query=${encodeURIComponent(query)}&limit=12`,
      );
      return data.results;
    },
    enabled,
  });
};
